import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Globe, Loader2, X, Check, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "storylo_admin_token";

/* ──────────────────────────────────────────────────────────────────────────
 * BatchPretranslatePanel
 *
 * Companion to BatchAutoWritePanel. Walks every published story and warms
 * the body + script translation caches for all 9 non-English languages.
 * After a successful run, social-media crawlers and first-time foreign
 * visitors see their language version instantly — no fresh ~3 s translate
 * wait. Combined with the article backfill, the whole catalogue is
 * indexed by Google's hreflang in 10 languages.
 *
 *  • Pill button in header opens a modal.
 *  • Modal shows pre-flight counts (eligible / fully-cached / no-source).
 *  • Confirm → backend processes 2 stories in parallel × 9 languages each.
 *  • Per-row results show body / script counts added.
 *
 * Auth: shares `storylo_admin_token` localStorage with SubmissionsPanel.
 * ────────────────────────────────────────────────────────────────────── */
export default function BatchPretranslatePanel({ reloadLibrary }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [eligible, setEligible] = useState(null);
  const [results, setResults] = useState(null);
  const [err, setErr] = useState("");
  const tokenRef = useRef(null);

  useEffect(() => {
    tokenRef.current = (typeof window !== "undefined" && window.localStorage.getItem(TOKEN_KEY)) || "";
  }, []);

  const ensureToken = () => {
    if (tokenRef.current) return tokenRef.current;
    const t = window.prompt("Admin token (Studio):", "storylo-admin");
    if (!t) return "";
    tokenRef.current = t;
    try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* blocked */ }
    return t;
  };

  const loadEligible = async () => {
    setErr("");
    setLoading(true);
    setResults(null);
    try {
      const token = ensureToken();
      if (!token) { setLoading(false); return; }
      const r = await axios.get(`${API}/admin/pretranslate-eligible`, {
        headers: { "X-Admin-Token": token },
      });
      setEligible(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* blocked */ }
        tokenRef.current = null;
        setErr("Admin token rejected. Click again to re-enter.");
      } else {
        setErr(e?.response?.data?.detail || "Couldn't load — try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onOpen = () => {
    setOpen(true);
    loadEligible();
  };

  const onClose = () => {
    if (running) return;
    setOpen(false);
    setResults(null);
    setErr("");
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, running]);

  const onRun = async () => {
    setErr("");
    setRunning(true);
    setResults(null);
    try {
      const token = ensureToken();
      if (!token) { setRunning(false); return; }
      const r = await axios.post(`${API}/admin/pretranslate-all-stories`, null, {
        headers: { "X-Admin-Token": token },
        // Generous timeout. 2 stories parallel × 9 langs each ≈ 8-12 s/pair.
        // 100 stories ≈ ~10 min worst case; we allow up to 15 min.
        timeout: 900000,
      });
      setResults(r.data);
      // Refresh the cached eligibility numbers so a close+reopen reflects
      // the new state without an extra round-trip.
      try {
        const e = await axios.get(`${API}/admin/pretranslate-eligible`, {
          headers: { "X-Admin-Token": token },
        });
        setEligible(e.data);
      } catch { /* non-fatal — old numbers stay */ }
      if (reloadLibrary) reloadLibrary();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Batch failed — try again.");
    } finally {
      setRunning(false);
    }
  };

  // Roughly estimate: each story takes ~10 s (9 body + 9 script LLM calls
  // running in parallel internally, then 2 stories at a time outside).
  const estimatedMinutes = eligible
    ? Math.max(1, Math.ceil((eligible.eligible_count * 10) / 2 / 60))
    : 0;

  return (
    <>
      <button
        onClick={onOpen}
        className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-white/15 hover:border-[color:var(--accent)]/60 hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1.5"
        data-testid="batch-pretranslate-open"
        title="Pre-translate every published story into all 9 non-English languages"
      >
        <Globe className="w-3.5 h-3.5" /> Translate library
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
          onClick={onClose}
          data-testid="batch-pretranslate-modal"
        >
          <div
            className="panel max-w-2xl w-full mx-auto my-12 md:my-6 p-6 md:p-7"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Pre-translate library"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5 inline-flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" /> Library translation
                </div>
                <div className="font-display text-xl leading-tight">
                  Pre-translate every story into 9 languages
                </div>
                <div className="text-xs text-[color:var(--muted)] mt-1.5 leading-relaxed">
                  Walks every <em>published</em> story and warms its translation cache for both the article and the script overlay across DE · FR · ES · IT · EL · TR · AR · NL · RU. After this finishes, foreign visitors and social-media crawlers see their own language instantly.
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-[color:var(--muted)] hover:text-white"
                data-testid="batch-pretranslate-close"
                aria-label="Close"
                disabled={running}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loading ? (
              <div className="py-12 text-center">
                <Loader2 className="w-6 h-6 mx-auto animate-spin text-[color:var(--accent)]" />
              </div>
            ) : err && !eligible ? (
              <div className="text-sm text-red-300 p-3 rounded border border-red-500/30 bg-red-500/10" data-testid="batch-pretranslate-error">
                {err}
              </div>
            ) : eligible ? (
              <>
                {/* Pre-flight summary — hidden once results arrive. */}
                {!results && (
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="panel p-3 border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">Need translating</div>
                      <div className="font-display text-2xl mt-1 text-[color:var(--accent)]" data-testid="batch-pretranslate-eligible-count">{eligible.eligible_count}</div>
                      <div className="text-[10px] text-[color:var(--muted)] mt-0.5">stories will be cached</div>
                    </div>
                    <div className="panel p-3 border border-white/5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">Fully cached</div>
                      <div className="font-display text-2xl mt-1">{eligible.fully_cached}</div>
                      <div className="text-[10px] text-[color:var(--muted)] mt-0.5">already done</div>
                    </div>
                    <div className="panel p-3 border border-white/5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">No source</div>
                      <div className="font-display text-2xl mt-1">{eligible.no_source}</div>
                      <div className="text-[10px] text-[color:var(--muted)] mt-0.5">no article / script</div>
                    </div>
                  </div>
                )}

                {!results && eligible.eligible_count > 0 && (
                  <div className="text-[11px] text-amber-200/90 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2 mb-5 inline-flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      Each story runs ~9–18 LLM translations in parallel (~10 s each), 2 stories at a time. Estimated time: ~{estimatedMinutes} {estimatedMinutes === 1 ? "minute" : "minutes"}. The modal stays open — leave the tab on and come back.
                    </span>
                  </div>
                )}

                {/* Results */}
                {results && (
                  <div className="mb-5" data-testid="batch-pretranslate-results">
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="panel p-3 border border-emerald-500/30 bg-emerald-500/5">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">Translated</div>
                        <div className="font-display text-2xl mt-1 text-emerald-300">{results.succeeded}</div>
                        <div className="text-[10px] text-emerald-200/70 mt-0.5">stories</div>
                      </div>
                      <div className="panel p-3 border border-zinc-500/30 bg-zinc-500/5">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-300">Skipped</div>
                        <div className="font-display text-2xl mt-1 text-zinc-300">{results.skipped}</div>
                      </div>
                      <div className="panel p-3 border border-red-500/30 bg-red-500/5">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-red-300">Failed</div>
                        <div className="font-display text-2xl mt-1 text-red-300">{results.failed}</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-[color:var(--muted)] mb-3">
                      Total caches added: <strong className="text-white/90">{results.body_added_total}</strong> body · <strong className="text-white/90">{results.script_added_total}</strong> script
                    </div>
                    {results.results && results.results.length > 0 && (
                      <ul className="space-y-1.5 max-h-56 overflow-auto pr-2 border-t border-white/5 pt-3">
                        {results.results.map((r) => (
                          <li key={r.id} className="text-xs flex items-start gap-2 leading-relaxed">
                            {r.status === "succeeded" && <Check className="w-3.5 h-3.5 text-emerald-300 mt-0.5 flex-shrink-0" />}
                            {r.status === "skipped" && <span className="w-3.5 h-3.5 inline-flex items-center justify-center text-zinc-400 flex-shrink-0">·</span>}
                            {r.status === "failed" && <AlertTriangle className="w-3.5 h-3.5 text-red-300 mt-0.5 flex-shrink-0" />}
                            <span className={r.status === "succeeded" ? "" : "text-[color:var(--muted)]"}>
                              <strong className="text-white/90">{r.title}</strong>
                              {r.status === "succeeded" && ` · ${r.body_added} body · ${r.script_added} script`}
                              {r.status === "succeeded" && r.partial_failures > 0 && ` · ${r.partial_failures} partial`}
                              {r.status === "skipped" && ` · ${r.reason}`}
                              {r.status === "failed" && ` · ${r.reason}`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {err && (
                  <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10 mb-3" data-testid="batch-pretranslate-error">{err}</div>
                )}

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-white/5">
                  <button
                    onClick={onClose}
                    disabled={running}
                    className="text-xs px-3 py-2 rounded-md border border-white/10 hover:border-white/30 transition-colors disabled:opacity-40"
                    data-testid="batch-pretranslate-cancel"
                  >
                    {results ? "Done" : "Cancel"}
                  </button>
                  {!results && (
                    <button
                      onClick={onRun}
                      disabled={running || eligible.eligible_count === 0}
                      className="text-sm px-4 py-2 rounded-md bg-[color:var(--accent)] text-black font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="batch-pretranslate-run"
                    >
                      {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                      {running
                        ? `Translating ${eligible.eligible_count}…`
                        : eligible.eligible_count === 0
                          ? "Nothing to translate"
                          : `Translate ${eligible.eligible_count} ${eligible.eligible_count === 1 ? "story" : "stories"}`}
                    </button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
